import PropTypes from 'prop-types';
import { useState } from 'react';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Avatar, Box, Chip, Stack, Typography, useMediaQuery } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
// utils
import { fDate } from '../../../utils/formatTime';

// ----------------------------------------------------------------------

// A plain 9/16 ratio scaled to the full `xl` container produced a hero roughly
// 860px tall on a desktop, pushing the whole page below the fold. The hero is
// now height-driven and clamped, so it stays a banner on every breakpoint.
const RootStyle = styled('div')(({ theme }) => ({
  position: 'relative',
  height: { xs: 340, sm: 420, md: 480, lg: 540 },
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[900],
}));

// The cover lives inside its own clipped layer so the drift animation can
// transform the frame without ever touching the image element itself. The
// animation only scales *up* from 1, so no edge of the frame is exposed, and it
// reverses on the alternate pass rather than snapping back to the start.
const MediaLayer = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  overflow: 'hidden',
  '@keyframes asap-kenburns': {
    from: { transform: 'scale(1) translate3d(0, 0, 0)' },
    to: { transform: 'scale(1.12) translate3d(-1.5%, -1%, 0)' },
  },
  animation: 'asap-kenburns 26s ease-in-out infinite alternate',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

const CoverImgStyle = styled('img')({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
});

// Shown only when the file itself fails to load, so a bad path never leaves the
// hero as an empty grey box.
const MediaFallback = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.35)} 0%, ${theme.palette.grey[900]} 70%)`,
  color: alpha(theme.palette.common.white, 0.35),
}));

// Scrim, tuned to hug only where copy sits instead of washing the whole frame:
// vertical on mobile (copy is at the bottom), directional from the left on
// desktop (copy is on the left), plus a light top wash for the chip row.
const ScrimStyle = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  pointerEvents: 'none',
  background: `linear-gradient(180deg, ${alpha(theme.palette.grey[900], 0.4)} 0%, ${alpha(
    theme.palette.grey[900],
    0.05
  )} 30%, ${alpha(theme.palette.grey[900], 0.68)} 80%, ${alpha(theme.palette.grey[900], 0.88)} 100%)`,
  [theme.breakpoints.up('md')]: {
    background: `linear-gradient(90deg, ${alpha(theme.palette.grey[900], 0.86)} 0%, ${alpha(
      theme.palette.grey[900],
      0.5
    )} 38%, ${alpha(theme.palette.grey[900], 0.12)} 72%, ${alpha(theme.palette.grey[900], 0)} 100%), linear-gradient(180deg, ${alpha(
      theme.palette.grey[900],
      0.42
    )} 0%, ${alpha(theme.palette.grey[900], 0)} 26%, ${alpha(theme.palette.grey[900], 0.3)} 76%, ${alpha(
      theme.palette.grey[900],
      0.68
    )} 100%)`,
  },
}));

// A whisper of brand colour along the bottom edge. Colour-graded rather than
// flat grey, which keeps the lower third from reading as dead space.
const BrandGlow = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  pointerEvents: 'none',
  opacity: 0.5,
  background: `linear-gradient(0deg, ${alpha(theme.palette.primary.main, 0.32)} 0%, ${alpha(
    theme.palette.primary.main,
    0
  )} 38%)`,
  [theme.breakpoints.up('md')]: {
    opacity: 0.35,
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(
      theme.palette.primary.main,
      0
    )} 55%)`,
  },
}));

const ContentStyle = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(2.5, 2.5, 3),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4, 4.5, 4),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(6, 7, 5.5),
  },
}));

const StatChip = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.6),
  height: 30,
  padding: theme.spacing(0, 1.25),
  borderRadius: 999,
  backgroundColor: alpha(theme.palette.common.white, 0.14),
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  color: theme.palette.common.white,
}));

// ----------------------------------------------------------------------

CourseHero.propTypes = {
  course: PropTypes.object.isRequired
};

export default function CourseHero({ course, ...other }) {
  const { image, name, introduction, tag, addedAt, subjectCount, videoCount } = course;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [imageFailed, setImageFailed] = useState(false);

  // The live detail payload does not always carry the counts, so guard both the
  // numbers and the image path rather than rendering "undefined".
  const stats = [
    { id: 'videos', icon: 'ic:round-play-circle', value: videoCount, label: 'videos' },
    { id: 'subjects', icon: 'ic:round-menu-book', value: subjectCount, label: 'subjects' },
  ].filter((stat) => Number.isFinite(stat.value));

  const showCover = Boolean(image) && !imageFailed;

  return (
    <RootStyle {...other}>
      <MediaLayer>
        {showCover ? (
          <CoverImgStyle
            src={image}
            alt={name || ''}
            loading="eager"
            decoding="async"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <MediaFallback>
            <Iconify icon="ic:round-menu-book" width={56} />
          </MediaFallback>
        )}
      </MediaLayer>

      <ScrimStyle />

      <BrandGlow />

      <ContentStyle>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
          {tag?.name && (
            <Chip
              label={tag.name}
              size="small"
              sx={{
                height: 28,
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                bgcolor: alpha(theme.palette.common.white, 0.92),
                color: 'text.primary',
              }}
            />
          )}

          {stats.map((stat) => (
            <StatChip key={stat.id}>
              <Iconify icon={stat.icon} width={15} />
              <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600 }}>
                {`${stat.value} ${stat.label}`}
              </Typography>
            </StatChip>
          ))}
        </Stack>

        <Box sx={{ mt: 'auto', maxWidth: 820 }}>
          <MotionInView variants={varFadeInUp}>
            <Typography
              component="h1"
              variant="h1"
              sx={{
                color: 'common.white',
                mb: introduction ? 1.5 : 2.5,
                fontSize: { xs: 27, sm: 36, md: 44, lg: 50 },
                lineHeight: 1.12,
                letterSpacing: -0.5,
                textShadow: '0 2px 14px rgba(0,0,0,0.4)',
              }}
            >
              {name}
            </Typography>

            {introduction && (
              <Typography
                variant="body1"
                sx={{
                  color: alpha(theme.palette.common.white, 0.86),
                  maxWidth: 640,
                  mb: 3,
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitLineClamp: isMobile ? 2 : 3,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.65,
                }}
              >
                {introduction}
              </Typography>
            )}
          </MotionInView>

          <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ alignItems: isMobile ? 'flex-start' : 'center' }}>
            {tag?.image && (
              <Avatar alt="" src={tag.image} sx={{ width: 44, height: 44, border: '2px solid rgba(255,255,255,0.85)' }} />
            )}

            <Box>
              {tag?.name && (
                <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 600 }}>
                  {tag.name}
                </Typography>
              )}
              {addedAt && (
                <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.72) }}>
                  {`Published ${fDate(addedAt)}`}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      </ContentStyle>
    </RootStyle>
  );
}
